/**
 * ScreenContextProvider - Exposes current screen state to AI
 *
 * Components can use useScreenContext() to tell the AI what's visible,
 * enabling context-aware intent detection.
 *
 * Example usage in a component:
 *   const { setScreenContext } = useScreenContext();
 *   useEffect(() => {
 *     setScreenContext({
 *       selectedDate: '2025-12-04',
 *       selectedClient: { id: 'xyz', name: 'TCNJ' },
 *       visibleAssignments: assignments.slice(0, 10),
 *       filters: { status: 'offered', location: 'TCNJ' }
 *     });
 *   }, [assignments, selectedDate]);
 */

import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ScreenContext = createContext(null);

export function ScreenContextProvider({ children }) {
  const [screenData, setScreenData] = useState({
    view: null,
    selectedDate: null,
    selectedClient: null,
    selectedLocation: null,
    selectedValet: null,
    visibleAssignments: [],
    visibleClients: [],
    filters: {},
    totals: {},
    // For assignment grid
    gridState: null,
    // For finance view
    financialSummary: null,
    // For events/calendar
    calendarDate: null,
    // Custom data from any component
    custom: {}
  });

  // Update screen context (merge with existing)
  const setScreenContext = useCallback((updates) => {
    setScreenData(prev => ({
      ...prev,
      ...updates,
      custom: {
        ...prev.custom,
        ...(updates.custom || {})
      }
    }));
  }, []);

  // Clear screen context (on navigation)
  const clearScreenContext = useCallback(() => {
    setScreenData({
      view: null,
      selectedDate: null,
      selectedClient: null,
      selectedLocation: null,
      selectedValet: null,
      visibleAssignments: [],
      visibleClients: [],
      filters: {},
      totals: {},
      gridState: null,
      financialSummary: null,
      calendarDate: null,
      custom: {}
    });
  }, []);

  // Expose to global for AgentChatBubble to read
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__ODV_SCREEN_CONTEXT__ = screenData;
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.__ODV_SCREEN_CONTEXT__;
      }
    };
  }, [screenData]);

  return (
    <ScreenContext.Provider value={{ screenData, setScreenContext, clearScreenContext }}>
      {children}
    </ScreenContext.Provider>
  );
}

export function useScreenContext() {
  const context = useContext(ScreenContext);
  if (!context) {
    // Return no-op if not wrapped in provider (graceful degradation)
    return {
      screenData: {},
      setScreenContext: () => {},
      clearScreenContext: () => {}
    };
  }
  return context;
}

export default ScreenContextProvider;
